import React, { useState, useEffect, useRef } from 'react';
import { round3 } from '../../utils/money';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { IcArrowLeft, IcBanknote, IcPrinter, IcPencil, IcTrash, IcCamera, IcImage, IcExternalLink, IcClose } from '../../theme/icons';
import factureService from '../../services/factureService';
import supplierService from '../../services/supplierService';
import authService from '../../services/authService';
import Swal from '../../theme/swal';
import KI from '../../theme/palette';
import { format } from 'date-fns';
import fr from 'date-fns/locale/fr';
import { useCompany } from '../../context/CompanyContext';
import PhoneLink from '../../components/PhoneLink';
import { printDocument } from '../../desktop/engine';
import { BtnSk, CardSkeleton } from '../../components/Skeleton';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const FactureDetails = ({ factureId = null, onClose = null }) => {
  // Rendu en page (/facturation/detail/:id) ou en panneau (`factureId` +
  // `onClose`, ex. depuis la fiche fournisseur) — même contenu.
  const params = useParams();
  const id = factureId ?? params.id;
  const embedded = typeof onClose === 'function';
  const navigate = useNavigate();
  const location = useLocation();
  // « Retour » revient d'où l'on vient quand la page le dit (ex. panneau
  // fournisseur : /suppliers?fiche=<id>), sinon sur la liste des factures.
  const backTo = (location.state && location.state.backTo) || '/facturation/list';
  const backToList = () => { if (embedded) onClose(); else navigate(backTo); };
  // Sellers can open a facture and print it, but not pay / modify / delete it.
  const canManage = authService.isManagerOrAdmin();
  const { settings: company } = useCompany();
  const [facture, setFacture] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  // Photo of the paper invoice — upload / camera capture
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    const fetchFactureData = async () => {
      setLoading(true);
      try {
        // The id parameter might actually be a barcode, try to get by either
        let factureData;
        try {
          factureData = await factureService.getFactureById(id);
        } catch (initialErr) {
          console.log('Could not fetch by ID, trying as barcode:', id);
          // If direct ID lookup fails, try to get all factures and filter by barcode
          const allFactures = await factureService.getAllFactures();
          factureData = allFactures.find(f => f.barrecode === id);

          if (!factureData) {
            throw new Error('Facture non trouvée avec ce code ou ID');
          }
        }

        setFacture(factureData);

        if (factureData.supplier) {
          try {
            const supplierData = await supplierService.getSupplierById(factureData.supplier);
            setSupplier(supplierData);
          } catch (err) {
            console.error('Error fetching supplier details:', err);
          }
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching facture details:', err);
        setError('Impossible de charger les détails de la facture');
        Swal.fire('Erreur', 'Impossible de charger les détails de la facture', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchFactureData();
  }, [id]);

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr });
    } catch (e) {
      return dateString || 'N/A';
    }
  };

  const getPaymentStatus = (facture) => {
    if (!facture) return { text: 'Non défini', className: 'bdg-neutral' };

    const amountDue = parseFloat(facture.amount_due);
    const total = parseFloat(facture.total);

    // Etiquettes-2 §3 : ✓ Payé · ◐ Partiel · ● Non payé
    if (facture.is_avoir) return { text: '↩ Avoir', className: 'bdg-warn' };
    if (amountDue <= 0) {
      return { text: '✓ Payé', className: 'bdg-pos' };
    } else if (amountDue < total) {
      return { text: '◐ Partiel', className: 'bdg-alert' };
    } else {
      return { text: '● Non payé', className: 'bdg-critical' };
    }
  };

  // Calculate totals from product details
  const calculateTotals = (facture) => {
    if (!facture || !facture.details) {
      return {
        subTotal: parseFloat(facture?.sub_total || 0),
        totalTVA: 0,
        totalTTC: parseFloat(facture?.total || 0)
      };
    }

    let subTotal = 0;
    let totalTVA = 0;

    const products = facture.details || facture.products || [];

    products.forEach(item => {
      const cost = parseFloat(item.cost || item.price || 0);
      const quantity = parseFloat(item.quantity || 1);
      const tvaRate = parseFloat(item.tva_rate || item.tva || 0);

      const itemTotal = cost * quantity;
      const itemTVA = itemTotal * (tvaRate / 100);

      subTotal += itemTotal;
      totalTVA += itemTVA;
    });

    const totalTTC = subTotal + totalTVA;

    return { subTotal, totalTVA, totalTTC };
  };

  const handlePayment = () => {
    if (!facture || facture.amount_due <= 0) return;
    setPaymentAmount(facture.amount_due.toString());
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!paymentAmount || isNaN(paymentAmount) || parseFloat(paymentAmount) <= 0) {
      Swal.fire('Erreur', 'Veuillez entrer un montant valide', 'error');
      return;
    }

    const amount = round3(parseFloat(paymentAmount));

    if (facture && amount > facture.amount_due) {
      Swal.fire('Erreur', 'Le montant ne peut pas dépasser le montant dû', 'error');
      return;
    }

    try {
      await factureService.updateFacturePayment(facture.id, { amount });
      setShowPaymentModal(false);
      Swal.fire('Succès', 'Paiement enregistré avec succès', 'success');

      // Refresh facture data
      const updatedFacture = await factureService.getFactureById(id);
      setFacture(updatedFacture);
    } catch (err) {
      console.error('Payment update error:', err);
      Swal.fire('Erreur', 'Impossible d\'enregistrer le paiement', 'error');
    }
  };

  const onPhotoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file || !facture) return;
    try {
      setUploadingPhoto(true);
      const updated = await factureService.uploadFactureImage(facture.id, file);
      setFacture((prev) => ({ ...prev, ...updated }));
    } catch (err) {
      console.error('Error uploading facture photo:', err);
      Swal.fire('Erreur', err?.error || err?.message || "Impossible d'enregistrer la photo", 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!facture?.image_url) return;
    const result = await Swal.fire({
      title: 'Supprimer la photo ?',
      text: 'La photo attachée à cette facture sera supprimée.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: KI.coralDark,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      focusCancel: true,
    });
    if (!result.isConfirmed) return;
    try {
      setUploadingPhoto(true);
      const updated = await factureService.deleteFactureImage(facture.id);
      setFacture((prev) => ({ ...prev, ...updated }));
    } catch (err) {
      console.error('Error deleting facture photo:', err);
      Swal.fire('Erreur', 'Impossible de supprimer la photo', 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!facture) return;

    try {
      await factureService.generatePDF(facture.id);
    } catch (err) {
      console.error('Error generating PDF:', err);
      Swal.fire('Erreur', 'Impossible de générer le PDF', 'error');
    }
  };

  const handleDeleteFacture = async () => {
    if (!facture) return;

    const result = await Swal.fire({
      title: 'Supprimer cette facture ?',
      html: `La facture <strong>#${facture.barrecode || facture.id}</strong> sera définitivement supprimée.<br/>Cette action est <strong>irréversible</strong>.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: KI.coralDark,
      cancelButtonColor: KI.faint,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    try {
      await factureService.deleteFacture(facture.id);
      await Swal.fire({
        title: 'Supprimée',
        text: 'La facture a été supprimée avec succès.',
        icon: 'success',
        timer: 1800,
        showConfirmButton: false,
      });
      backToList();
    } catch (err) {
      console.error('Error deleting facture:', err);
      Swal.fire('Erreur', 'Erreur lors de la suppression de la facture.', 'error');
    }
  };

  if (loading) {
    return (
      <div className={embedded ? 'p-8 flex justify-center items-center' : 'min-h-screen bg-gray-100 p-8 flex justify-center items-center'}>
        <CardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className={embedded ? 'w-full' : 'min-h-screen bg-gray-100 p-8'}>
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
          <p className="font-bold">Erreur</p>
          <p>{error}</p>
          {!embedded && (
            <Link to={backTo} className="mt-4 inline-flex items-center px-4 py-2 btn-s">
              <IcArrowLeft className="mr-2" /> Retour
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (!facture) {
    return (
      <div className={embedded ? 'w-full' : 'min-h-screen bg-gray-100 p-8'}>
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md">
          <p className="font-bold">Information</p>
          <p>Aucune facture trouvée avec cet identifiant.</p>
          {!embedded && (
            <Link to={backTo} className="mt-4 inline-flex items-center px-4 py-2 btn-s">
              <IcArrowLeft className="mr-2" /> Retour
            </Link>
          )}
        </div>
      </div>
    );
  }

  const paymentStatus = getPaymentStatus(facture);
  const items = (facture.details || facture.products) || [];
  const totals = calculateTotals(facture);
  const subTotal = facture.sub_total != null && facture.sub_total !== '' ? parseFloat(facture.sub_total) : totals.subTotal;
  const totalTVA = facture.total_tva != null && facture.total_tva !== '' ? parseFloat(facture.total_tva) : totals.totalTVA;
  // The stored facture.total INCLUDES the timbre fiscal (flat, not subject to
  // TVA). Split it back out so the displayed rows sum exactly:
  //   Total TTC (HT + TVA) + Timbre = Total facture.
  const timbre = facture.timbre != null && facture.timbre !== '' ? parseFloat(facture.timbre) || 0 : 0;
  // Frais de livraison — hors total facture (comptés comme charge en stats).
  const livraison = parseFloat(facture.livraison) || 0;
  const totalFacture = facture.total != null && facture.total !== '' ? parseFloat(facture.total) : totals.totalTTC + timbre;
  const totalTTC = totalFacture - timbre;
  const amountPaid = parseFloat(facture.amount_paid || 0);
  const amountDue = parseFloat(facture.amount_due || 0);
  const factureDate = formatDate(facture.date_added || facture.date_created || facture.date);

  return (
    <div className={embedded ? 'w-full print:bg-white' : 'w-full px-4 pt-2 pb-4 print:bg-white print:p-0'}>
      {/* Print styles: hide chrome, flatten the sheet, keep colors.
          En panneau : seul le panneau facture (.facture-print-root) s'imprime,
          la liste et la fiche fournisseur derrière restent masquées. */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          ${embedded ? `
          body * { visibility: hidden; }
          .facture-print-root, .facture-print-root * { visibility: visible; }
          .facture-print-root { position: absolute !important; inset: 0 !important; padding: 0 !important; background: #fff !important; overflow: visible !important; }
          .facture-print-root > div { max-height: none !important; overflow: visible !important; box-shadow: none !important; border-radius: 0 !important; }
          ` : ''}
          .invoice-sheet { box-shadow: none !important; border: none !important; margin: 0 !important; max-width: 100% !important; border-radius: 0 !important; }
          .print-color { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 14mm; }
        }
      `}</style>

      {/* Toolbar (not part of the printed document) */}
      <div className="no-print max-w-4xl mx-auto mb-4 flex flex-wrap items-center justify-between gap-3">
        {embedded ? <span /> : (
          <Link to={backTo} className="inline-flex items-center px-5 py-2.5 text-sm btn-s">
            <IcArrowLeft className="mr-2" /> Retour
          </Link>
        )}
        <div className="flex items-center gap-2">
          {canManage && amountDue > 0 && (
            <button
              onClick={handlePayment}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm btn-p"
            >
              <IcBanknote /> Enregistrer un paiement
            </button>
          )}
          <button
            onClick={() => printDocument('a4')}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm btn-s"
          >
            <IcPrinter /> Imprimer
          </button>
          {/* « Modifier » et la corbeille vivent sous la facture, entre la feuille
              et « Photo de la facture » (owner 2026-09-10). */}
        </div>
      </div>

      {/* ===== The invoice sheet ===== */}
      <div className="invoice-sheet max-w-4xl mx-auto bg-white shadow-lg rounded-lg border border-gray-200 p-8 md:p-12">
        {/* Header: store identity + invoice meta */}
        <div className="flex flex-col sm:flex-row justify-between gap-6 pb-6 border-b border-gray-200">
          <div className="flex items-start gap-3">
            {company && company.logo_url && (
              <img src={`${BASE_URL}${company.logo_url}`} alt="logo" className="h-14 w-14 object-contain" />
            )}
            <div>
              <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">{(company && company.nom) || 'Kipara'}</h1>
              <p className="text-sm text-gray-500 mt-1">{facture.is_avoir ? 'Retour fournisseur' : "Facture d'achat fournisseur"}</p>
              {company && (
                <div className="text-xs text-gray-500 mt-2 leading-relaxed">
                  {company.adresse && <p className="whitespace-pre-line">{company.adresse}</p>}
                  {company.matricule_fiscale && <p>MF : {company.matricule_fiscale}</p>}
                  {(company.telephones || []).filter((t) => t).map((t, i) => <p key={i}>Tél : {t}</p>)}
                  {company.email && <p>{company.email}</p>}
                  {company.website && <p>{company.website}</p>}
                  {company.rib && <p>RIB : {company.rib}</p>}
                  {company.bank_name && <p>Banque : {company.bank_name}</p>}
                </div>
              )}
            </div>
          </div>
          <div className="sm:text-right">
            <h2 className="text-xl font-bold text-gray-800">{facture.is_avoir ? 'FACTURE AVOIR' : 'FACTURE'}</h2>
            <p className="text-sm text-gray-600 mt-1">N° {facture.number || facture.barrecode || 'N/A'}</p>
            {facture.barrecode && (
              <p className="text-xs text-gray-400">Code : {facture.barrecode}</p>
            )}
            <p className="text-sm text-gray-600 mt-1">Date : {factureDate}</p>
            <span className={`print-color bdg mt-2 ${paymentStatus.className}`}>
              {paymentStatus.text}
            </span>
          </div>
        </div>

        {/* Fournisseur */}
        <div className="py-6 border-b border-gray-200">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Fournisseur</p>
          {supplier ? (
            <div className="text-sm text-gray-700 leading-relaxed">
              <p className="text-base font-bold text-gray-900">{supplier.name}</p>
              {supplier.address && <p>{supplier.address}</p>}
              <p className="text-gray-500">
                {supplier.phone && <span>Tél : <PhoneLink phone={supplier.phone} /></span>}
                {supplier.phone && supplier.email && <span className="mx-2">·</span>}
                {supplier.email && <span>{supplier.email}</span>}
              </p>
            </div>
          ) : (
            <p className="text-base font-bold text-gray-900">{facture.supplier_name || 'N/A'}</p>
          )}
        </div>

        {/* Items table */}
        <div className="py-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-300 text-gray-500">
                <th className="py-2 text-left font-semibold">Désignation</th>
                <th className="py-2 text-right font-semibold">P.U. HT</th>
                <th className="py-2 text-center font-semibold">Qté</th>
                <th className="py-2 text-right font-semibold">Remise</th>
                <th className="py-2 text-right font-semibold">TVA</th>
                <th className="py-2 text-right font-semibold">Total HT</th>
                <th className="py-2 text-right font-semibold">Total TTC</th>
              </tr>
            </thead>
            <tbody>
              {items.length > 0 ? (
                items.map((item, index) => {
                  const cost = parseFloat(item.cost || item.price || 0);
                  const quantity = parseFloat(item.quantity || 1);
                  const tvaRate = parseFloat(item.tva_rate || item.tva || 0);
                  const isDiscount = item.is_discount || false;
                  // Convention du module facturation : coût 0 = produit gratuit,
                  // même si l'ancien enregistrement n'a pas le flag is_free.
                  const isFree = item.is_free || cost === 0;
                  // Produit gratuit = remise 100% (le backend stocke déjà cost=0 / 100%)
                  const remisePct = isFree ? 100 : (isDiscount ? parseFloat(item.discount_percentage || 0) : 0);
                  const originalCost = parseFloat(item.original_cost || 0);
                  const totalHT = cost * quantity;
                  const totalLineTTC = totalHT + totalHT * (tvaRate / 100);

                  return (
                    <tr key={index} className="border-b border-gray-100 align-top">
                      <td className="py-3 pr-2">
                        <div className="font-medium text-gray-900">{item.name || item.product_name}</div>
                        {item.code && <div className="text-xs text-gray-400">Code : {item.code}</div>}
                      </td>
                      {/* P.U. HT = prix AVANT remise (le prix remisé se lit
                          dans Total HT). */}
                      <td className="py-3 text-right text-gray-600 whitespace-nowrap">
                        {(remisePct > 0 && originalCost > 0 ? originalCost : cost).toFixed(3)}
                      </td>
                      <td className="py-3 text-center text-gray-700">{quantity}</td>
                      {/* Remise en écriture normale, même gris que les autres
                          colonnes (demande Ahmed 2026-08-20). */}
                      <td className="py-3 text-right text-gray-600 whitespace-nowrap">
                        {remisePct > 0 ? `${remisePct}%` : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-3 text-right text-gray-600 whitespace-nowrap">{tvaRate}%</td>
                      <td className="py-3 text-right text-gray-700 whitespace-nowrap">{totalHT.toFixed(3)}</td>
                      <td className="py-3 text-right font-medium text-gray-900 whitespace-nowrap">{totalLineTTC.toFixed(3)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="py-6 text-center text-gray-400">
                    Les détails des produits ne sont pas disponibles.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-full sm:w-80">
            <div className="flex justify-between py-2 text-sm text-gray-600 border-b border-gray-100">
              <span>Total HT</span>
              <span className="font-medium text-gray-800">{subTotal.toFixed(3)} DT</span>
            </div>
            <div className="flex justify-between py-2 text-sm text-gray-600 border-b border-gray-100">
              <span>TVA</span>
              <span className="font-medium text-gray-800">{totalTVA.toFixed(3)} DT</span>
            </div>
            <div className="flex justify-between py-2 text-sm text-gray-600 border-b border-gray-100">
              <span>Total TTC</span>
              <span className="font-medium text-gray-800">{totalTTC.toFixed(3)} DT</span>
            </div>
            {timbre > 0 && (
              <div className="flex justify-between py-2 text-sm text-gray-600 border-b border-gray-100">
                <span>Timbre fiscal <span className="text-[10px] text-gray-400">(hors TVA)</span></span>
                <span className="font-medium text-gray-800">{timbre.toFixed(3)} DT</span>
              </div>
            )}
            <div className="flex justify-between py-3 text-base font-bold text-gray-900 border-b-2 border-gray-300">
              <span>{facture.is_avoir ? 'Total avoir' : 'Total facture'}</span>
              <span>{totalFacture.toFixed(3)} DT</span>
            </div>
            {livraison > 0 && (
              <div className="flex justify-between py-2 text-sm text-gray-600 border-b border-gray-100">
                <span>Livraison <span className="text-[10px] text-gray-400">(charge, hors total)</span></span>
                <span className="font-medium text-gray-800">{livraison.toFixed(3)} DT</span>
              </div>
            )}
            {facture.is_avoir ? (<>
              {/* Avoir : pas de paiement — son montant est déduit des factures du fournisseur */}
              <div className="flex justify-between py-2 text-sm text-gray-600">
                <span>Déduit des factures</span>
                <span className="font-medium text-gray-800">
                  {(facture.avoir_detail || []).reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0).toFixed(3)} DT
                </span>
              </div>
              {(facture.avoir_detail || []).length > 0 && (
                <div className="py-1 text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-1">
                  {facture.avoir_detail.map((d) => (
                    <Link key={d.facture} to={`/facturation/detail/${d.facture}`} className="no-print hover:text-gray-900 hover:underline">
                      Facture #{d.facture} · {(parseFloat(d.amount) || 0).toFixed(3)} DT
                    </Link>
                  ))}
                </div>
              )}
              <div className="flex justify-between py-2 text-sm">
                <span className="font-semibold text-gray-700">Reste d’avoir</span>
                <span className="font-bold text-gray-800">{(parseFloat(facture.avoir_remaining) || 0).toFixed(3)} DT</span>
              </div>
            </>) : (<>
            <div className="flex justify-between py-2 text-sm text-gray-600">
              <span>Montant payé</span>
              <span className="font-medium text-green-600">{amountPaid.toFixed(3)} DT</span>
            </div>
            {(parseFloat(facture.avoir_applied) || 0) > 0 && (
              <div className="flex justify-between py-2 text-sm text-gray-600">
                <span>Avoir déduit <span className="text-[10px] text-gray-400">(retour fournisseur)</span></span>
                <span className="font-medium text-gray-800">{(parseFloat(facture.avoir_applied) || 0).toFixed(3)} DT</span>
              </div>
            )}
            <div className="flex justify-between py-2 text-sm">
              <span className="font-semibold text-gray-700">Reste à payer</span>
              <span className={`font-bold ${amountDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {amountDue.toFixed(3)} DT
              </span>
            </div>
            </>)}
          </div>
        </div>

        {/* Notes */}
        {facture.notes && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Notes</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{facture.notes}</p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-gray-200 text-center text-xs text-gray-400">
          {(company && company.nom) || 'Kipara'} · Document généré le {factureDate}
        </div>
      </div>

      {/* Actions sur la facture — entre la feuille et « Photo de la facture »
          (owner 2026-09-10) : corbeille à GAUCHE, « Modifier » à DROITE. */}
      {canManage && (
        <div className="no-print max-w-4xl mx-auto mt-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleDeleteFacture}
            title="Supprimer la facture"
            aria-label="Supprimer la facture"
            className="btn-db w-9 h-9 rounded-md"
          >
            <IcTrash />
          </button>
          <Link
            to={`/facturation/edit/${id}`}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm btn-g-edit"
          >
            <IcPencil /> Modifier
          </Link>
        </div>
      )}

      {/* Hidden pickers for the facture photo: camera capture + gallery */}
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhotoFile} />
      <input ref={galleryInputRef} type="file" accept="image/*" className="hidden" onChange={onPhotoFile} />

      {/* Photo of the paper invoice (not part of the printed document) */}
      <div className="no-print max-w-4xl mx-auto mt-6 bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-bold text-gray-900">Photo de la facture</h3>
            <p className="text-xs text-gray-400 mt-0.5">Photo ou scan de la facture papier du fournisseur</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={uploadingPhoto}
              onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
              className="btn-g-edit px-4 py-2 text-sm"
            >
              <IcCamera /> Prendre une photo
            </button>
            <button
              type="button"
              disabled={uploadingPhoto}
              onClick={() => galleryInputRef.current && galleryInputRef.current.click()}
              className="btn-g-edit px-4 py-2 text-sm"
            >
              <IcImage /> {facture.image_url ? "Remplacer l'image" : 'Importer une image'}
            </button>
          </div>
        </div>

        {uploadingPhoto ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
            <BtnSk />
            Enregistrement de la photo…
          </div>
        ) : facture.image_url ? (
          <div>
            <a
              href={facture.image_url}
              target="_blank"
              rel="noreferrer"
              title="Ouvrir la photo en grand"
              className="relative block rounded-xl overflow-hidden border border-gray-200 max-w-md mx-auto"
            >
              <img
                src={facture.image_url}
                alt={`Photo de la facture ${facture.number || facture.barrecode}`}
                className="w-full max-h-96 object-contain bg-gray-50"
              />
              <span className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/60 text-white text-xs font-medium">
                <IcExternalLink className="text-[10px]" /> Ouvrir
              </span>
            </a>
            {canManage && (
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={handleDeletePhoto}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm btn-db"
                >
                  <IcTrash /> Supprimer la photo
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-xl border-2 border-dashed border-gray-200 py-10 text-center">
            <IcImage className="mx-auto text-3xl text-gray-200 mb-2" />
            <p className="text-sm text-gray-500">Aucune photo attachée à cette facture</p>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="no-print modal-backdrop flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            {/* Composants-2 §10 : entête aplat encre + ✕ */}
            <div className="modal-head -mx-6 -mt-6 mb-4 rounded-t-lg">
              <h2 className="modal-title text-xl">Enregistrer un paiement</h2>
              <button type="button" onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose size={17} /></button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Fournisseur: {supplier?.name || facture.supplier_name || 'N/A'}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                Total: {parseFloat(facture.total).toFixed(3)} DT
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Reste à payer: {parseFloat(facture.amount_due).toFixed(3)} DT
              </p>
            </div>

            <form onSubmit={handlePaymentSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montant à payer
                </label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  min="0"
                  max={facture.amount_due}
                  step="0.001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 text-sm btn-g"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm btn-p"
                >
                  Confirmer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FactureDetails;
